import { ApplicationState } from '@/store';
import { completeOnboardRequest } from '@/store/onboard/actions';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ImageName, OnboardingWhiteLabelItem } from './OnboardingWhiteLabelItem';
import { OnboardingWhiteLabelNavigator } from './OnboardingWhiteLabelNavigator';
import './styles.scss';

interface OnboardingStateType {
    imageName: ImageName;
    description: string;
    title: string;
    showOnPageDots: boolean;
}

interface OnboardingWhiteLabelProps {
    handleModalClose?: () => void;
}
const OnboardingWhiteLabel: React.FC<OnboardingWhiteLabelProps> = ({ handleModalClose = () => {} }: OnboardingWhiteLabelProps) => {
    const { strings } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
    }));

    const dispatch = useDispatch();

    const [currentStageIndex, setCurrentStageIndex] = useState(0);

    const onboardingStages = useMemo<OnboardingStateType[]>(
        () => [
            {
                imageName: 'welcome',
                title: strings.onboardingWhiteLabel.WELCOME_TITLE,
                description: strings.onboardingWhiteLabel.WELCOME_DESCRIPTION,
                showOnPageDots: false,
            },
            {
                imageName: 'protagonism',
                title: strings.onboardingWhiteLabel.PROTAGONISM_TITLE,
                description: strings.onboardingWhiteLabel.PROTAGONISM_DESCRIPTION,
                showOnPageDots: true,
            },
            {
                imageName: 'recommendations',
                title: strings.onboardingWhiteLabel.RECOMMENDATIONS_TITLE,
                description: strings.onboardingWhiteLabel.RECOMMENDATIONS_DESCRIPTION,
                showOnPageDots: true,
            },
            {
                imageName: 'virtualPartner',
                title: strings.onboardingWhiteLabel.VIRTUAL_PARTNER_TITLE,
                description: strings.onboardingWhiteLabel.VIRTUAL_PARTNER_DESCRIPTION,
                showOnPageDots: true,
            },
            {
                imageName: 'final',
                title: strings.onboardingWhiteLabel.FINAL_TITLE,
                description: strings.onboardingWhiteLabel.FINAL_DESCRIPTION,
                showOnPageDots: false,
            },
        ],
        [strings],
    );

    const numberOfDots = useMemo(() => {
        return onboardingStages.filter(({ showOnPageDots }) => showOnPageDots).length;
    }, [onboardingStages]);

    const finishedOnboarding = useMemo(() => {
        return currentStageIndex === onboardingStages.length - 1;
    }, [onboardingStages, currentStageIndex]);

    const shownBigButtonOnNavigator = useMemo(() => {
        return finishedOnboarding || currentStageIndex === 0;
    }, [finishedOnboarding, currentStageIndex]);

    const onComplete = useCallback(() => {
        dispatch(completeOnboardRequest([]));
        handleModalClose();
    }, []);

    const onNext = useCallback(() => {
        setCurrentStageIndex((prev) => prev + 1);
    }, []);

    const onPreview = useCallback(() => {
        setCurrentStageIndex((prev) => prev - 1);
    }, []);

    return (
        <div className="onboarding-whitelabel-container">
            <div className="stage-container">
                {onboardingStages.map(({ description, title, imageName }, index) => (
                    <div className={`stage-item-container ${currentStageIndex === index && 'current'}`} key={`${imageName}-${index}`}>
                        <OnboardingWhiteLabelItem description={description} imageName={imageName} title={title} />
                    </div>
                ))}
            </div>
            <OnboardingWhiteLabelNavigator
                currentPageInDots={currentStageIndex - 1}
                finished={finishedOnboarding}
                numberDots={numberOfDots}
                onComplete={onComplete}
                onNext={onNext}
                onPreview={onPreview}
                shownBigButton={shownBigButtonOnNavigator}
            />
        </div>
    );
};

export { OnboardingWhiteLabel };
